import type { MarketplaceProduct, MarketplaceReview } from '@/types';
import { withBackend } from '@/services/api/backend';

/**
 * Marketplace catalog reads. Real source: `listMarketplaceListings` / `getListingReviewsPublic`
 * (firebase-functions-endeeko, apps/endeeko/storefront/store.marketplace.ts + social-pending), which
 * read the SAME `storefront_listings` / `company_reviews` collections every seller-side storefront page
 * already writes — a real cross-company browse over real listings, not a separate mock catalog.
 * `withBackend` keeps the seed as a safety net if the live call fails (VITE_USE_BACKEND off, or a
 * transient error) so the page never breaks.
 */
const REVIEWS_KEY = 'endeeko.marketplace.reviews';

const now = new Date().toISOString();
const img = (seed: string) => `https://picsum.photos/seed/${seed}/640/420`;

// --- Seed catalog (fallback only — served when the live backend call fails) -------------------------
const SEED: MarketplaceProduct[] = [
  {
    id: 'mp_kube', companyId: 'cs_brightcloud', companyName: 'BrightCloud Systems',
    name: 'Managed Kubernetes', description: 'Production-grade managed Kubernetes clusters with 24/7 SRE coverage, autoscaling, and observability built in.',
    category: 'Data Processing, Hosting, and Related Services', industryId: 'Information', type: 'service',
    price: 499, tokenPrice: 120, currency: 'usd', images: [img('kube1'), img('kube2')],
    deliveryTime: '3–5 business days', features: ['Autoscaling', '24/7 SRE', 'Multi-region', '99.95% SLA'], isDigital: true,
    createdAt: now, updatedAt: now, stats: { views: 1240, purchases: 86, averageRating: 4.6, reviewsCount: 3 },
  },
  {
    id: 'mp_observ', companyId: 'cs_brightcloud', companyName: 'BrightCloud Systems',
    name: 'Observability Suite', description: 'Logs, metrics, and traces in one dashboard with anomaly alerts.',
    category: 'Data Processing, Hosting, and Related Services', industryId: 'Information', type: 'product',
    price: 149, tokenPrice: 40, currency: 'usd', images: [img('observ1')],
    stock: 9999, features: ['Unlimited dashboards', 'Anomaly alerts', 'API access'], isDigital: true,
    createdAt: now, updatedAt: now, stats: { views: 870, purchases: 210, averageRating: 4.3, reviewsCount: 2 },
    recentDailySales: [6, 8, 5, 7, 9, 6, 7],
  },
  {
    id: 'mp_webapp', companyId: 'cs_pixelforge', companyName: 'PixelForge Studio',
    name: 'Custom Web App Build', description: 'End-to-end design and development of a custom web application for your business.',
    category: 'Computer Systems Design and Related Services', industryId: 'Professional, Scientific, and Technical Services', type: 'service',
    price: 12000, currency: 'usd', images: [img('webapp1'), img('webapp2')],
    deliveryTime: '6–10 weeks', features: ['Discovery workshop', 'UI/UX design', 'Full-stack build', '3 months support'], isDigital: false,
    createdAt: now, updatedAt: now, stats: { views: 540, purchases: 12, averageRating: 4.9, reviewsCount: 2 },
  },
  {
    id: 'mp_designkit', companyId: 'cs_pixelforge', companyName: 'PixelForge Studio',
    name: 'Design System Kit', description: 'A production-ready Figma + React design system to accelerate your product team.',
    category: 'Computer Systems Design and Related Services', industryId: 'Professional, Scientific, and Technical Services', type: 'product',
    price: 799, tokenPrice: 200, currency: 'usd', images: [img('designkit1')],
    stock: 4, features: ['Figma library', 'React components', 'Tokens', 'Docs'], isDigital: true,
    createdAt: now, updatedAt: now, stats: { views: 410, purchases: 64, averageRating: 4.1, reviewsCount: 1 },
    recentDailySales: [2, 1, 3, 2, 2, 1, 2],
  },
  {
    id: 'mp_telehealth', companyId: 'cs_medibridge', companyName: 'MediBridge Care',
    name: 'Telehealth Platform', description: 'White-label telemedicine platform with scheduling, video visits, and e-prescriptions.',
    category: 'Ambulatory Health Care Services', industryId: 'Health Care and Social Assistance', type: 'product',
    price: 199, tokenPrice: 60, currency: 'usd', images: [img('tele1')],
    stock: 50, features: ['Video visits', 'Scheduling', 'E-prescriptions', 'HIPAA-ready'], isDigital: true,
    createdAt: now, updatedAt: now, stats: { views: 980, purchases: 140, averageRating: 4.4, reviewsCount: 2 },
    recentDailySales: [5, 4, 6, 5, 7, 5, 6],
  },
  {
    id: 'mp_smarthome', companyId: 'cs_voltretail', companyName: 'Volt Retail',
    name: 'Smart-Home Bundle', description: 'A curated bundle of smart-home devices with setup guide.',
    category: 'Electronics and Appliance Retailers', industryId: 'Retail Trade', type: 'product',
    price: 299, tokenPrice: 80, currency: 'usd', images: [img('smarthome1'), img('smarthome2')],
    stock: 3, features: ['5 devices', 'Hub included', '1-year warranty'], isDigital: false,
    createdAt: now, updatedAt: now, stats: { views: 760, purchases: 95, averageRating: 4.2, reviewsCount: 1 },
    recentDailySales: [3, 4, 2, 5, 3, 4, 3],
  },
];

const SEED_REVIEWS: MarketplaceReview[] = [
  { id: 'rv1', productId: 'mp_kube', userId: 'u1', userName: 'Dana W.', rating: 5, comment: 'Excellent uptime and the SRE team is fast and reliable. Highly recommend.', createdAt: now },
  { id: 'rv2', productId: 'mp_kube', userId: 'u2', userName: 'Marco P.', rating: 4, comment: 'Great platform, setup was quick. A bit pricey but good quality.', createdAt: now },
];

interface MarketplaceCard {
  id: string; companyId: string; companyName: string; companySlug: string | null; companyLogo: string | null;
  name: string; image: string | null; type: 'product' | 'service'; category: string; subcategory: string | null;
  description: string; price: number; currency: string; variants: { name: string; price: number; sku: string | null; stockCount: number | null }[];
  ratingAvg: number; ratingCount: number; likes: number; views: number; purchases: number; updatedAt: string;
}

function fromCard(c: MarketplaceCard): MarketplaceProduct {
  return {
    id: c.id, companyId: c.companyId, companyName: c.companyName, companyLogo: c.companyLogo ?? undefined, companySlug: c.companySlug,
    name: c.name, description: c.description, category: c.category, industryId: '', type: c.type,
    price: c.price, currency: 'usd', images: c.image ? [c.image] : [], variants: c.variants, features: [], isDigital: false,
    createdAt: c.updatedAt, updatedAt: c.updatedAt,
    stats: { views: c.views, purchases: c.purchases, averageRating: c.ratingAvg, reviewsCount: c.ratingCount },
  };
}

interface PublicReviewRow { id: string; reviewerName: string; rating: number; content: string; createdAt: string }

function localReviews(): MarketplaceReview[] {
  try {
    return JSON.parse(localStorage.getItem(REVIEWS_KEY) ?? '[]') as MarketplaceReview[];
  } catch {
    return [];
  }
}

let cache: Promise<MarketplaceProduct[]> | null = null;
function loadAll(): Promise<MarketplaceProduct[]> {
  if (!cache) {
    cache = withBackend<{ listings: MarketplaceCard[] } | MarketplaceProduct[]>('listMarketplaceListings', { limit: 200 }, async () => SEED)
      .then((data) => (Array.isArray(data) ? data : data.listings.map(fromCard)));
  }
  return cache;
}

export const marketplaceService = {
  listAll: loadAll,

  getById: async (id: string): Promise<MarketplaceProduct | null> => {
    return (await loadAll()).find((p) => p.id === id) ?? null;
  },

  listReviews: async (productId: string): Promise<MarketplaceReview[]> => {
    const product = (await loadAll()).find((p) => p.id === productId);
    if (!product) return [];
    return withBackend<{ reviews: PublicReviewRow[] } | MarketplaceReview[]>(
      'getListingReviewsPublic', { companyId: product.companyId, listingId: productId, limit: 50 },
      async () => [...SEED_REVIEWS, ...localReviews()].filter((r) => r.productId === productId).sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
    ).then((data) => (Array.isArray(data)
      ? data
      : data.reviews.map((r): MarketplaceReview => ({ id: r.id, productId, userId: r.id, userName: r.reviewerName, rating: r.rating, comment: r.content, createdAt: r.createdAt }))));
  },

  /** Real submission — same public review path as the storefront listing page (no proof-of-purchase gate; none of Endeeko's reviews require one). */
  addReview: async (input: Omit<MarketplaceReview, 'id' | 'createdAt'>): Promise<MarketplaceReview> => {
    const product = (await loadAll()).find((p) => p.id === input.productId);
    if (product?.companySlug) {
      const res = await withBackend<{ id: string; success: boolean } | null>(
        'submitCompanyReview',
        { slug: product.companySlug, listingId: input.productId, reviewerName: input.userName, rating: input.rating, content: input.comment },
        async () => null,
      );
      if (res) return { id: res.id, productId: input.productId, userId: input.userId, userName: input.userName, rating: input.rating, comment: input.comment, createdAt: new Date().toISOString() };
    }
    // Fallback (seed data / product without a real company slug): keep the localStorage behavior.
    const review: MarketplaceReview = { ...input, id: `rv_${Date.now()}`, createdAt: new Date().toISOString() };
    const next = [...localReviews(), review];
    localStorage.setItem(REVIEWS_KEY, JSON.stringify(next));
    return review;
  },
};
